import { z } from 'zod'
import { roleSchema } from './role.js'

export const genderSchema = z.enum(['M', 'F', 'X'])
export type Gender = z.infer<typeof genderSchema>

// ── Race ─────────────────────────────────────────────────────────────────────

export const raceSchema = z.enum(['chinese', 'indian', 'malay', 'other'])
export type Race = z.infer<typeof raceSchema>

// ── HolidayGroup ─────────────────────────────────────────────────────────────

export const holidayGroupSchema = z.enum([
  'chinese_new_year',
  'deepavali',
  'thaipusam',
  'wesak',
  'christmas',
  'hari_raya_aidilfitri',
  'hari_raya_aidiladha',
  'maulidur_rasul',
  'nuzul_al_quran',
  'awal_muharram',
])
export type HolidayGroup = z.infer<typeof holidayGroupSchema>

// ── Religion ─────────────────────────────────────────────────────────────────

export const religionSchema = z.enum([
  'islam',
  'buddhist',
  'hindu',
  'christian',
  'other',
])

export type Religion = z.infer<typeof religionSchema>

// ── Employment type ─────────────────────────────────────────────────────────

export type EmploymentType = 'full_time' | 'contractual'

export interface EmploymentTypeUpdateRequest {
  employmentType: EmploymentType
}

export interface EmploymentTypeUpdateResponse {
  employee: Employee
  propagation: {
    touchedBalances: number
    warnings: { employeeId: string; leaveType: string; code: string }[]
  }
}

export const employmentTypeSchema = z.enum(['full_time', 'contractual'])

export const employeeSchema = z.object({
  id: z.guid(),
  email: z.string().email(),
  /**
   * Slack profile email override (migration 0153) — a copy of `email`
   * unless HR set it because the employee's Slack workspace email differs
   * (e.g. lou@ytl.com vs lou@frogasia.com). Loose string (not .email())
   * so the PATCH body can carry '' meaning "reset to a copy of email".
   * Optional so pre-migration fixtures still parse.
   */
  slackEmail: z.string().nullable().optional(),
  name: z.string().min(1),
  // Optional so existing fixtures/mocks that omit it still satisfy the type;
  // the API (EmployeeOut) always returns it, so real rows carry the value.
  internalId: z.string().nullable().optional(),
  preferredName: z.string().nullable(),
  teamId: z.guid(),
  role: roleSchema,
  jobTitle: z.string().min(1),
  religion: religionSchema.nullable(),
  race: raceSchema.nullable(),
  hometown: z.string().nullable(),
  distanceKm: z.number().int().nonnegative().nullable(),
  married: z.boolean(),
  children: z.number().int().nonnegative(),
  yearsOfService: z.number().nonnegative(),
  onProbation: z.boolean(),
  joinDate: z.string().date(),
  birthDate: z.string().date(),
  gender: genderSchema,
  employmentType: employmentTypeSchema,
  annualEntitlement: z.number().int().nonnegative(),
  // True when the employee has an annual LeaveCreditOverride. An override is a
  // flat grant that bypasses accrual, so the home AccrualBar hides when set.
  // Optional for backward-compat with fixtures/rows that pre-date the field.
  hasAnnualOverride: z.boolean().optional(),
  /**
   * The standing employee-scoped PRIMARY approver — the line manager. Sourced
   * from ``approval_assignments`` via the resolver. Replaces ``reportsTo``
   * (column dropped in alembic 0077). Nullable when the employee has no
   * standing primary assignment yet.
   */
  primaryApproverId: z.guid().nullable(),
  /**
   * Every active team-scoped HOD_OVERSIGHT approver for the employee's team.
   * Replaces the inverted ``managedTeamIds`` field (column dropped in
   * alembic 0077). Empty list when no HOD oversight is configured.
   */
  hodApproverIds: z.array(z.guid()),
  isManager: z.boolean(),
  isHod: z.boolean(),
  /** ID of the linked auth-table user row, set on first sign-in. */
  authUserId: z.string().nullable(),
  active: z.boolean(),
  /**
   * Multi-role assignment — the full set of roles this employee holds (e.g.
   * ['manager', 'hr']). `role` (primary) is derived as the highest-ranked
   * entry. Optional so existing API responses from pre-multi-role servers
   * still parse without error; consumers should treat undefined as [].
   */
  roles: z.array(roleSchema).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Booking-window fields (migration 0056). Optional so existing API responses
  // from pre-migration servers still parse without error.
  /** HR-granted override: when true the advance-booking cap is lifted. */
  bookingWindowOverride: z.boolean().optional().default(false),
  /** Probation end date — caps the booking window for probationary employees. */
  probationEndDate: z.string().date().nullable().optional().default(null),
  // Contract end date (migration 0068). Optional so existing API responses
  // from pre-migration servers still parse without error.
  /** Fixed-term contract end date. Null for permanent employees. */
  contractEndDate: z.string().date().nullable().optional().default(null),
  // Last working day (migration 0116). Optional so existing API responses
  // from pre-migration servers still parse without error.
  /** Resignation last-working-day. Distinct from contractEndDate (fixed-term
   *  contracts only) — applies to any employee type. Null when no
   *  resignation is on file. */
  lastWorkingDay: z.string().date().nullable().optional().default(null),
  /** Leave-rhythm band (§2.3) for the list-row avatar, mirroring the manager
   *  roster. Populated by GET /employees (list); absent on other sources.
   *  Optional (no default) so it stays off the required shape — existing
   *  Employee fixtures/mocks don't need to supply it. */
  health: z
    .object({
      band: z.enum(['low', 'medium', 'high']),
      contributingPatterns: z.array(z.string()).default([]),
    })
    .nullable()
    .optional(),
})

export type Employee = z.infer<typeof employeeSchema>

/** POST /employees body. Only identity + role-shaping fields are required;
 *  bookkeeping fields (religion, hometown, distanceKm, married, children,
 *  isManager, isHod, active, authUserId) default sensibly so the HR form
 *  doesn't have to ask.
 *
 *  ``hodApproverIds`` is **not** settable from this body — HOD oversight
 *  lives in ``approval_assignments`` (TEAM scope) and is written via the
 *  HR admin surface (future slice). The ``isManager`` / ``isHod`` flags
 *  remain on the form as decorative HR labels.
 *
 *  ``primaryApproverId`` IS accepted on create — when provided, the server
 *  writes a PRIMARY ``approval_assignment`` in the same transaction as the
 *  employee row, so the new hire lands wired into the org-chart graph.
 *  Optional (null = orphan node, HR can grant later via Edit Mode).
 *
 *  `annualEntitlement` and `yearsOfService` are derived server-side
 *  (yearsOfService from joinDate, annualEntitlement from the policy
 *  table given employmentType + onProbation) — clients should omit them. */
export const employeeCreateSchema = employeeSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    annualEntitlement: true,
    yearsOfService: true,
    hodApproverIds: true,
  })
  .extend({
    religion: religionSchema.nullable().default(null),
    race: raceSchema.nullable().default(null),
    hometown: z.string().nullable().default(null),
    distanceKm: z.number().int().nonnegative().nullable().default(null),
    married: z.boolean().default(false),
    children: z.number().int().nonnegative().default(0),
    isManager: z.boolean().default(false),
    isHod: z.boolean().default(false),
    active: z.boolean().default(true),
    authUserId: z.string().nullable().default(null),
    primaryApproverId: z.guid().nullable().default(null),
  })

export type EmployeeCreate = z.infer<typeof employeeCreateSchema>

/** PATCH body — every field optional. authUserId is intentionally NOT
 *  patchable from this route (it's set by the auth-link hook).
 *  ``hodApproverIds`` stays read-only (server-derived). ``primaryApproverId``
 *  IS patchable — when sent, the route replaces the employee's PRIMARY
 *  ``approval_assignments`` row (null clears it, leaving an orphan node). */
export const employeeUpdateSchema = employeeSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    authUserId: true,
    primaryApproverId: true,
    hodApproverIds: true,
  })
  .partial()
  .extend({
    // Override the optional fields to accept explicit null (clearing the date).
    probationEndDate: z.string().date().nullable().optional(),
    bookingWindowOverride: z.boolean().optional(),
    // contractEndDate can be explicitly null to clear a previously set date.
    contractEndDate: z.string().date().nullable().optional(),
    // lastWorkingDay can be explicitly null to clear a withdrawn resignation.
    lastWorkingDay: z.string().date().nullable().optional(),
    // Primary approver. Omit to leave unchanged; null clears it.
    primaryApproverId: z.guid().nullable().optional(),
  })

export type EmployeeUpdate = z.infer<typeof employeeUpdateSchema>

/** GET /employees/directory-stats — at-a-glance counts for the HR Employees
 *  directory filter cards. All counts are over active, non-system employees
 *  (the directory's default population), so the tile numbers line up with an
 *  unfiltered list. ``anniversaryThisMonth`` counts work anniversaries —
 *  employees whose join-date month equals the current month. */
export const employeeDirectoryStatsSchema = z.object({
  contractual: z.number().int().nonnegative(),
  probationers: z.number().int().nonnegative(),
  anniversaryThisMonth: z.number().int().nonnegative(),
})

export type EmployeeDirectoryStats = z.infer<typeof employeeDirectoryStatsSchema>
